import { ChangeEvent, useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useOnboarding } from "../../contexts/OnboardingContext";
import OnboardingLayout from "./OnboardingLayout";

interface UserProfileStepProps {
  onContinue: (name: string, profileImage: File | null, description: string) => void;
  initialName: string;
  initialDescription?: string;
}

// Function to create a file from a Blob
const createFileFromBlob = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};

// Function to get crop area pixels
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<{ file: File; url: string }> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  // Set canvas size to 512x512
  canvas.width = 512;
  canvas.height = 512;

  // Draw the cropped image on the canvas
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, 512, 512);

  // As a blob with compression
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Canvas is empty"));

        const file = createFileFromBlob(blob, "cropped-image.jpeg");
        const url = URL.createObjectURL(blob);

        resolve({ file, url });
      },
      "image/jpeg",
      0.8
    );
  });
};

export default function UserProfileStep({ onContinue, initialName, initialDescription = "" }: UserProfileStepProps) {
  const { data, setData } = useOnboarding();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [image, setImage] = useState<File | null>(data.profileImage || null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isCropping, setIsCropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result as string);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCropSave = async () => {
    if (imageSrc && croppedAreaPixels) {
      try {
        const { file, url } = await getCroppedImg(imageSrc, croppedAreaPixels);
        setImage(file);
        setData((prev) => ({ ...prev, profileImage: file }));
        setImageSrc(url);
        setIsCropping(false);
      } catch (error) {
        console.error("Error cropping image:", error);
      }
    }
  };

  const handleCropCancel = () => {
    setImageSrc(null);
    setIsCropping(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Update context data when input changes
  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    setData((prev) => ({ ...prev, name: newName }));
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    setData((prev) => ({ ...prev, description: newDescription }));
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImageSrc(null);
    setData((prev) => ({ ...prev, profileImage: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <OnboardingLayout
      title="Tell others a bit about yourself"
      onContinue={() => onContinue(name, image, description)}
      canContinue={name.trim().length > 0 && !isCropping}
      step={5}
      totalSteps={5}
    >
      <div className="space-y-6">
        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">Profile picture (optional)</p>

          {isCropping ? (
            <div>
              <div className="relative h-52 sm:h-60 w-full">
                <Cropper
                  image={imageSrc || ""}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
              <div className="mt-3 flex justify-between">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCropSave}
                  className="bg-blue-600 py-2 px-3 border border-transparent rounded-md shadow-sm text-sm leading-4 font-medium text-white hover:bg-blue-700"
                >
                  Save Crop
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 mx-auto sm:mx-0 border-2 border-dashed rounded-full flex items-center justify-center overflow-hidden"
                style={{ borderColor: imageSrc ? "transparent" : "#d1d5db" }}
              >
                {imageSrc ? (
                  <img src={imageSrc} alt="Profile preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>

              <div className="flex flex-col items-center sm:items-start">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50"
                >
                  {imageSrc ? "Change picture" : "Upload picture"}
                </button>
                {imageSrc && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Your name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={handleNameChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your name"
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={handleDescriptionChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Share a bit about yourself"
            rows={3}
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}
